/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// tslint:disable:max-func-body-length
import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    // IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout';

import { Alert, Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { IActionContext, RichTextComponent } from '@msdyn365-commerce/core';
import { CartLine, ProductSearchCriteria, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { IDobbiesNewCheckoutProps } from './dobbies-new-checkout.props.autogenerated';
import { IDobbiesNewCheckoutData } from './dobbies-new-checkout.data';
import GuidedFormComponent, { ICheckoutGuidedFormProps } from './components/guided-form';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { ICheckoutModulesSharedState } from '../../actions/checkout-modules-state-share';
import { getSearchByCriteriaAsync, GetSearchByCriteriaInput } from '../../themes/dobbies/actions/search-by-criteria';
import { getRestrictedProducts } from '../../themes/dobbies/utilities/post-code-restriction/get-restricted-products';
import { _getScreenSize } from '../../themes/dobbies/utilities/display-utils';
import { UpdateCheckoutShippingAddressInput } from '../../themes/dobbies/actions/update-shipping-address-again';
import { trackTrace } from '../../themes/dobbies/utilities/logging';
/* VSI Customization - START - 19/04/21
 * Following interface is used to pass product related info to LineItem component
 * to display error message for post code restriction */
interface IProductsDeliveryDetails {
    postCodeValidation?: ICheckoutModulesSharedState;
    cartLines?: CartLine[];
    plantProductsIds?: number[];
    postCodeErrorMsg?: string;
    actionContext?: IActionContext;
}
interface IProductDeliveryInfo {
    plantPostCodeInfo?: IProductsDeliveryDetails;
}
type CheckoutSlots = 'checkoutInformation' | 'orderConfirmation' | 'termsAndConditions' | 'newCheckoutInformation';
/* VSI Customization - END */

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

const isZipCodeRestricted = (ctx: IActionContext, productId: number) => {
    const enteredZipCookie = ctx.requestContext.cookies.get('entered_zip_code');
    let enteredZipCode = '';
    if (enteredZipCookie.value) {
        enteredZipCode = enteredZipCookie.value as string;
    }
    const restrictedPostCodesInfoCookie = ctx.requestContext.cookies.get('restricted_post_codes_info');

    if (enteredZipCode) {
        const restrictedPostCodesInfo = restrictedPostCodesInfoCookie.value;
        if (Array.isArray(restrictedPostCodesInfo)) {
            const rpProductObj = restrictedPostCodesInfo.find(rpi => rpi['productId'] === productId);
            if (rpProductObj) {
                const postCodes = rpProductObj['restrictedPostCodes'];
                if (Array.isArray(postCodes)) {
                    const hasZipCode = postCodes.find(pc => enteredZipCode.startsWith(pc));
                    if (hasZipCode) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
};

export const LineItemComponent: React.FC<ILineItem & IProductsDeliveryDetails> = ({
    LineItem,
    item,
    LineId,
    pickUpAtStore,
    cartLines,
    plantProductsIds,
    postCodeErrorMsg,
    postCodeValidation,
    actionContext
}) => {
    /* VSI Customization - START - 19/04/21 - Display error msg against cartlines incase of post code retriction error */
    let postCodeError = <></>;

    if (!pickUpAtStore && (postCodeValidation?.invalidAddressForCartProducts || postCodeValidation?.notBtPostCode)) {
        // First find this cartLine Id in cartLines to get its product Id
        const cartLine = cartLines?.find(cartLineItem => {
            return cartLineItem.LineId === LineId;
        });
        const cartLineProductId = cartLine?.ProductId;
        // Now check if it is plant then show the message
        const isPlantProduct = plantProductsIds?.find(productId => productId === cartLineProductId);
        if (actionContext && cartLineProductId && isZipCodeRestricted(actionContext, cartLineProductId)) {
            postCodeError = isPlantProduct ? (
                <Alert isOpen={!!(postCodeErrorMsg && postCodeErrorMsg.length > 0)} color='danger' assertive={true}>
                    {postCodeErrorMsg}
                </Alert>
            ) : (
                postCodeError
            );
        }
    }
    /* VSI Customization - END */
    return (
        <>
            <Node {...LineItem}>
                {item}
                <Alert isOpen={true} color='danger' assertive={true}>
                    {postCodeError}
                </Alert>
                {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
            </Node>
        </>
    );
};

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup & IProductsDeliveryDetails> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    cartLines,
    plantProductsIds,
    postCodeValidation,
    postCodeErrorMsg,
    actionContext
}) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent
                    key={lineItem.LineId}
                    {...lineItem}
                    cartLines={cartLines}
                    plantProductsIds={plantProductsIds}
                    postCodeValidation={postCodeValidation}
                    postCodeErrorMsg={postCodeErrorMsg}
                    actionContext={actionContext}
                />
            ))}
        </Node>
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems & IProductDeliveryInfo> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    plantPostCodeInfo
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsForShip && <LineItemGroupComponent {...itemsForShip} {...plantPostCodeInfo} />}
    </Node>
);

//Customization removed because its now available out of the box in .29 version
// // const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines}, {_checkoutCart}) => (
// export const _renderOrderSummarylines = (orderSummary: IOrderSummary, props: ICheckoutProps<{}>): JSX.Element | null => {
//     // let a = _cart;
//     // const taxAmount = props.cart && props.cart.cart.TaxAmount || 0;\
//     // @ts-ignore
//     const shippingAmount = props.data && props.data.checkout.result._checkoutCart._cart.ShippingChargeAmount;
//     // @ts-ignore
//     const subtotalAmount = props.data && props.data.checkout.result._checkoutCart._cart.SubtotalAmount;
//     // @ts-ignore
//     const subtotalSalesAmount = props.data && props.data.checkout.result._checkoutCart._cart.SubtotalSalesAmount;

//     const modifiedSubtoalAmount: number = Number(shippingAmount) + (Number(subtotalAmount) - subtotalSalesAmount);

//     /* VSI Customization - START*/
//     // @ts-ignore
//     const shippingDefaultText = props.data.checkout.result._checkoutCart._cart.DeliveryMode !== '' ? '£0.00' : 'To Be calculated';
//     const shippingText = shippingAmount > 0 ? `£${modifiedSubtoalAmount.toFixed(2)}` : shippingDefaultText;
//     /* VSI Customization - END*/

//     return (
//         <div className='msc-order-summary-wrapper'>
//             {orderSummary.heading}
//             <div className='msc-order-summary__items'>
//                 {orderSummary.lines && (
//                     /* VSI Customization - START*/
//                     // TODO: need to fix harcoded values
//                     // Customization Reason: Subtotal amount is adding shipping charges
//                     <>
//                         <p className='msc-order-summary__line-sub-total'>
//                             <span className='msc-order-summary__label'>{'Sub Total'}</span>
//                             <span className='msc-price msc-order-summary__value'>
//                                 <span className='msc-price__actual'>{`£${subtotalSalesAmount.toFixed(2)}`}</span>
//                             </span>
//                         </p>
//                         <p className='msc-order-summary__line-shipping'>
//                             <span className='msc-order-summary__label'>{'Home Delivery'}</span>
//                             <span className='msc-price msc-order-summary__value'>
//                                 <span className='msc-price__actual'>{shippingText}</span>
//                             </span>
//                         </p>
//                         {orderSummary.lines.otherCharge}
//                         {orderSummary.lines.loyalty}
//                         {orderSummary.lines.giftCard}
//                         {orderSummary.lines.orderTotal}
//                     </>
//                     /* VSI Customization - END */
//                 )}
//             </div>
//         </div>
//     );
// };
/* VSI Customization - START - 18/11/20 */
// const getSlotItems = (key: string, props: ICheckoutViewProps): React.ReactNode[] => {

//     const { slots } = props;
//     return slots && slots[key] && slots[key].length ? slots[key] : [];
// };
const getSlotItems = (key: CheckoutSlots, props: IDobbiesNewCheckoutProps<IDobbiesNewCheckoutData>): React.ReactNode[] =>
    props.slots[key] ?? [];

const getId = (item: React.ReactNode): string => {
    return get(item, 'props.id') || '';
};

// Following function gets product attributes and set in state
const _getProductAttributes = async (
    ctx: IActionContext,
    products: SimpleProduct[],
    setPlantProducts: React.Dispatch<React.SetStateAction<number[] | undefined>>
) => {
    // Filter plants products and send it to LineItems Component to display error msg against it incase postcode is not valid
    const productIds: number[] = [];
    products.map(product => productIds.push(product.RecordId));

    const callerContext = ctx;
    const {
        requestContext: {
            apiSettings: { channelId, catalogId }
        }
    } = ctx;
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = { ChannelId: channelId, CatalogId: catalogId };
    searchCriteriaInput.Ids = productIds;
    searchCriteriaInput.IncludeAttributes = true;

    const actionInput: GetSearchByCriteriaInput = {
        productIds: productIds,
        context: callerContext,
        pageType: 'checkout',
        dataCacheType: () => 'application',
        getCacheKey: () => 'postCodeRestriction',
        getCacheObjectType: () => 'postCodeRestriction'
    };
    const productsSearchResult = await getSearchByCriteriaAsync(actionInput, callerContext);

    const plantsProducts: number[] = await getRestrictedProducts(productsSearchResult, ctx.requestContext.apiSettings.baseUrl);

    // Store plant product RecordIds in state
    setPlantProducts(plantsProducts);
};
/* VSI Customization - END */
// const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
//     <div className='msc-order-summary-wrapper'>
//         <div className='msc-order-summary__content'>
//             {heading}
//             <div className='msc-order-summary__items'>
//                 {lines && (
//                     <>
//                         {lines.subtotal}
//                         {lines.shipping}
//                         {lines.otherCharge}
//                         {lines.tax}
//                         {lines.loyalty}
//                         {lines.giftCard}
//                         {lines.orderTotal}
//                     </>
//                 )}
//             </div>
//         </div>
//     </div>
// );
const CheckoutView: React.FC<ICheckoutViewProps & IDobbiesNewCheckoutProps<IDobbiesNewCheckoutData> & ICheckoutGuidedFormProps> = props => {
    const {
        config: { breadCrumbsRoot, breadCrumbs, postCodeErrorMsgForShippingAddress /*, postCodeErrorMsgForCartLines */ },
        context: { actionContext },
        canShow,
        checkoutProps,
        // headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        // sideProps,
        orderConfirmation,
        loading,
        alert,
        // title,
        // orderSummary,
        // lineItems,
        resources,
        placeOrderButton,
        data: {
            checkoutModulesSharedState: { result: checkoutModulesSharedState },
            // accordionExpandedState,
            checkoutAccordionStep
        }
    } = props;

    // const checkout = props.data.checkout?.result;
    /* VSI Customization - START - 18/11/20 */
    const [plantProducts, setPlantProducts] = useState<number[] | undefined>();
    const [totalCartItems, setTotalCartItems] = useState(0);
    const [totalCartCost, setTotalCartCost] = useState('');
    const [showFullPageLoader, setShowFullPageLoader] = useState(false);

    // Check for pv=1 query parameter to show full page loader
    useEffect(() => {
        if (MsDyn365.isBrowser) {
            const urlParams = new URLSearchParams(window.location.search);
            const pvParam = urlParams.get('pv');
            setShowFullPageLoader(pvParam === '1');
        }
    }, []);
    useEffect(() => {
        if (!plantProducts) {
            props.data.products.then(productss => {
                _getProductAttributes(actionContext, productss, setPlantProducts).catch(error => props.telemetry.trace(error));
            });
        }
    });

    useEffect(() => {
        getCheckoutState(actionContext)
            .then(checkoutState => {
                const numOfItems = checkoutState.checkoutCart.cart.TotalItems ? checkoutState.checkoutCart.cart.TotalItems : 0;
                setTotalCartItems(numOfItems);
                const totalAmountText = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currencyDisplay: 'symbol',
                    currency: 'GBP',
                    minimumFractionDigits: 2
                }).format(checkoutState.checkoutCart.cart.TotalAmount || 0);
                setTotalCartCost(totalAmountText);
            })
            .catch(e => {
                console.error('Error getting checkout state', e);
            });
    }, []);
    let items = getSlotItems('checkoutInformation', props);
    if (props.config.enableNewCheckout) {
        items = getSlotItems('newCheckoutInformation', props);
    }

    let creditBannerFound = false;
    const checkoutItems = items;
    // store text block in credit-banner
    if (items) {
        for (const item of items) {
            if (!creditBannerFound) {
                const childId = getId(item);
                if (childId.includes('text-block')) {
                    creditBannerFound = true;
                    break;
                }
            }
        }
    }
    // const creditBannerSection = creditBanner && React.cloneElement(creditBanner as React.ReactElement);

    // Customization for Post code restriction to display msg against cartLine
    // const cartLines = checkout && checkout.checkoutCart.cart.CartLines;

    // const plantPostCodeInfoObject: IProductDeliveryInfo | undefined = {
    //     plantPostCodeInfo: {
    //         postCodeValidation: {
    //             invalidAddressForCartProducts: checkoutModulesSharedState?.invalidAddressForCartProducts,
    //             notBtPostCode: checkoutModulesSharedState?.notBtPostCode
    //         },
    //         cartLines: cartLines,
    //         plantProductsIds: plantProducts,
    //         postCodeErrorMsg: postCodeErrorMsgForCartLines,
    //         actionContext: props.context.actionContext
    //     }
    // };
    const renderCheckouttHeader = () => {
        // const totalAmount = orderSummary?.lines?.customerAccount;
        return (
            <div className='header-total'>
                <h2>Checkout</h2>
                <div className='header-total__count'>
                    <span className='header-total__count-items'>
                        {totalCartItems} {totalCartItems === 1 ? 'item' : 'items'}
                    </span>
                    <span className='header-total__count-price'>{totalCartCost}</span>
                </div>
            </div>
        );
    };
    // const { isAuthenticated } = props.context.request.user;
    let hasHomeDelProducts = false;
    let hasCnCProducts = false;
    props.data?.checkout?.result?.checkoutCart.cart.CartLines?.map(cl => {
        if (!cl.FulfillmentStoreId) {
            hasHomeDelProducts = true;
        }
        if (cl.FulfillmentStoreId) {
            hasCnCProducts = true;
        }
    });
    const placeOrderContent = (
        <>
            {placeOrderButton}
            <RichTextComponent text={props.config.checkoutPlaceorderText} />
        </>
    );
    const [screenSize, setScreenSize] = useState(_getScreenSize(props.context));
    // useEffect(() => {}, [accordionExpandedState, checkoutAccordionStep.result?.currentStep]);
    /* VSI Customization - END - 30/11/20 */

    useEffect(() => {
        if (!MsDyn365.isBrowser) {
            return;
        }

        setScreenSize(_getScreenSize(props.context));

        const handleResize = () => {
            const newScreenSize = _getScreenSize(props.context);
            setScreenSize(newScreenSize);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [props.context]);

    let isDefaultAddressSet = false;

    const defaultAddressString = props.context.request.app.config.defaultGuestCustomerAddressString;
    const hasHomeDelItem = props.data.checkout.result?.checkoutCart?.cart.CartLines?.some(cl => !cl.FulfillmentStoreId);
    if (defaultAddressString && hasHomeDelItem) {
        const splittedDefaultAddress = defaultAddressString.split(',');
        if (splittedDefaultAddress && splittedDefaultAddress.length >= 3) {
            const defaultZip = splittedDefaultAddress[2];
            if (
                props.data.checkout.result?.checkoutCart?.cart.ShippingAddress?.ZipCode === defaultZip &&
                checkoutAccordionStep.result?.currentStep === 3
            ) {
                isDefaultAddressSet = true;
            }
            if (
                props.data.checkout.result?.checkoutCart?.cart.CartLines?.some(cl => cl.ShippingAddress?.ZipCode === defaultZip) &&
                checkoutAccordionStep.result?.currentStep === 3
            ) {
                isDefaultAddressSet = true;
            }
        }
    }
    if (
        props.data.checkout.result?.checkoutCart?.cart.ShippingAddress?.Email ||
        props.data.checkout.result?.checkoutCart?.cart.CartLines?.some(cl => cl.ShippingAddress?.Email)
    ) {
        const lineZipCodes: string[][] = [];
        props.data.checkout.result?.checkoutCart?.cart.CartLines?.map(cl => {
            if (cl.ShippingAddress?.ZipCode && cl.ProductId && cl.ShippingAddress?.Email) {
                const em = cl.ShippingAddress?.Email;
                const splittedEmail = em.split('@');
                if (splittedEmail.length > 1) {
                    lineZipCodes.push([`${cl.ProductId}`, cl.ShippingAddress?.ZipCode, splittedEmail[0]]);
                }
            }
        });

        const splittedCartHeaderEmail = props.data.checkout.result?.checkoutCart?.cart.ShippingAddress?.Email?.split('@');
        const splittedCheckoutEmail = props.data.checkout.result?.shippingAddress?.Email?.split('@');
        const headerEmail = splittedCartHeaderEmail && splittedCartHeaderEmail?.length > 1 ? splittedCartHeaderEmail[0] : '';
        props.context.actionContext.telemetry.information(
            'User is checking out with following address details',
            props.data.checkout.result?.checkoutCart?.cart.ShippingAddress?.ZipCode,
            headerEmail,
            splittedCheckoutEmail && splittedCheckoutEmail.length > 1 ? splittedCheckoutEmail[0] : '',
            props.data.checkout.result?.shippingAddress?.ZipCode,
            lineZipCodes
        );

        const isOnlyCNC = props.data.checkout.result?.checkoutCart?.cart.CartLines?.find(cl => !cl.FulfillmentStoreId) ? false : true;
        const logMsg = `User is checking out with following address details On Place Order: HeaderZip: ${
            props.data.checkout.result?.checkoutCart?.cart.ShippingAddress?.ZipCode
        },
        Checkout level email: ${splittedCheckoutEmail && splittedCheckoutEmail.length > 1 ? splittedCheckoutEmail[0] : ''},
        Checkout Level Zipcode: ${props.data.checkout.result?.shippingAddress?.ZipCode},
        Cartline level details: ${lineZipCodes},
        Is Only CNC: ${isOnlyCNC},
        Is User Guest: ${!props.context.request.user.isAuthenticated},
        cartline delivery modes: ${props.data.checkout.result?.checkoutCart?.cart.CartLines?.map(cl => cl.DeliveryMode).join(',')}`;

        trackTrace(logMsg);
    }

    return (
        <Module {...checkoutProps}>
            {showFullPageLoader && (
                <div className='full-page-loader'>
                    <Waiting className='msc-waiting-circular msc-waiting-lg' />
                    <p>Please wait...</p>
                </div>
            )}
            {/* {!hasSalesOrder && <Node {...headerProps}>{title}</Node>} */}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            {props.config.enableNewCheckout ? (
                                <>
                                    {renderCheckouttHeader()}
                                    {screenSize !== 3 && screenSize !== 4 ? (
                                        <div className='ms-checkout__accordions'>
                                            {checkoutItems.map((item, index) => {
                                                if (!hasHomeDelProducts && index === 1) {
                                                    return <></>;
                                                }
                                                if (!hasCnCProducts && index === 2) {
                                                    return <></>;
                                                }
                                                return <>{item}</>;
                                            })}
                                            {checkoutAccordionStep.result?.currentStep === 3 && !isDefaultAddressSet && placeOrderContent}
                                            {isDefaultAddressSet ? (
                                                <div className='invalid-shipping-address'>{props.config.invalidDeliveryAddressMsg}</div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='ms-checkout__accordions--container'>
                                            <div className='ms-checkout__accordions'>
                                                {checkoutItems.map((item, index) => {
                                                    if (index === 0) {
                                                        return <></>;
                                                    }
                                                    if (!hasHomeDelProducts && index === 1) {
                                                        return <></>;
                                                    }
                                                    if (!hasCnCProducts && index === 2) {
                                                        return <></>;
                                                    }
                                                    return <>{item}</>;
                                                })}
                                                {checkoutAccordionStep.result?.currentStep === 3 &&
                                                    !isDefaultAddressSet &&
                                                    placeOrderContent}
                                                {isDefaultAddressSet ? (
                                                    <div className='invalid-shipping-address'>
                                                        {props.config.invalidDeliveryAddressMsg}{' '}
                                                        <a
                                                            href='#checkout-step-1'
                                                            onClick={() => {
                                                                props.context.actionContext.update(
                                                                    new UpdateCheckoutShippingAddressInput(),
                                                                    { updateShippingAddress: true }
                                                                );
                                                            }}
                                                        >
                                                            Edit Shipping Address
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                            <div className='ms-checkout__accordions ms-checkout__accordions--right'>
                                                {checkoutItems && checkoutItems.length ? checkoutItems[0] : <></>}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Node {...mainProps}>
                                    {/* VSI Customization - START - 18/11/20 */}
                                    <GuidedFormComponent
                                        {...{
                                            items: checkoutItems,
                                            moduleState: props.moduleState,
                                            disableGuidedCheckoutFlow: props.disableGuidedCheckoutFlow,
                                            resource: resources,
                                            isMobile: props.isMobile,
                                            isEditor: false,
                                            rootLink: breadCrumbsRoot,
                                            breadCrumbsText: breadCrumbs,
                                            invalidAddressForCartProducts: checkoutModulesSharedState?.invalidAddressForCartProducts,
                                            notBtPostCodeForPlants: checkoutModulesSharedState?.notBtPostCode,
                                            isFirstAddress: checkoutModulesSharedState?.isFirstAddress,
                                            loggedInUserNewAddress: checkoutModulesSharedState?.loggedInUserNewAddress,
                                            postCodeRestrictionMsg: postCodeErrorMsgForShippingAddress
                                        }}
                                    />
                                    {/* VSI Customization - END */}
                                </Node>
                            )}
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
